import React from 'react';
import './IncidentTimeline.css';

/**
 * IncidentTimeline - Componente de tracking visual de estados de incidencia
 * Muestra el progreso tipo "seguimiento de envío" (Amazon/Uber style)
 * 
 * @param {Object} incidencia - Objeto con datos de la incidencia
 * @param {Array} historial - Array de cambios históricos
 */
function IncidentTimeline({ incidencia, historial }) {
  if (!incidencia) return null;

  // Definir los 3 estados del pipeline
  const ESTADOS_PIPELINE = [
    {
      key: 'reportada',
      label: 'Reportada',
      icon: '📝',
      estados: ['pendiente'],
      descripcion: 'Incidencia creada por el vecino'
    },
    {
      key: 'asignada',
      label: 'Técnico Asignado',
      icon: '👷',
      estados: ['asignada', 'en_proceso'],
      descripcion: 'Proveedor/técnico asignado al caso'
    },
    {
      key: 'resuelta',
      label: 'Resuelta',
      icon: '✅',
      estados: ['resuelta'],
      descripcion: 'Problema solucionado'
    }
  ];

  // Determinar el índice del estado actual
  const estadoActualIndex = ESTADOS_PIPELINE.findIndex(step => 
    step.estados.includes(incidencia.estado)
  );

  // Obtener información adicional del historial
  const getStepDetails = (stepKey, stepIndex) => {
    const details = {
      fecha: null,
      nota: null,
      proveedor: null
    };

    // Para el primer paso (reportada), usar fecha de creación
    if (stepIndex === 0) {
      details.fecha = incidencia.fecha_creacion;
      details.nota = `Creada por ${incidencia.creador_nombre || 'usuario'}`;
      return details;
    }

    // Buscar en el historial el cambio relevante
    if (historial && historial.length > 0) {
      // Para asignación de técnico
      if (stepKey === 'asignada' && incidencia.proveedor_nombre) {
        const asignacion = historial.find(h => 
          h.tipo_cambio === 'proveedor_asignado' || 
          h.descripcion?.includes('proveedor') ||
          h.descripcion?.includes('asignado')
        );
        if (asignacion) {
          details.fecha = asignacion.fecha_cambio;
        }
        details.nota = `Asignado a ${incidencia.proveedor_nombre}`;
        details.proveedor = incidencia.proveedor_nombre;
      }

      // Para resolución
      if (stepKey === 'resuelta') {
        const resolucion = historial.find(h => 
          h.nuevo_valor === 'resuelta' || 
          h.descripcion?.toLowerCase().includes('resuelta')
        );
        if (resolucion) {
          details.fecha = resolucion.fecha_cambio;
          details.nota = 'Incidencia cerrada exitosamente';
        }
      }
    }

    return details;
  };

  // Formatear fecha
  const formatFecha = (fechaStr) => {
    if (!fechaStr) return null;
    const fecha = new Date(fechaStr);
    const hoy = new Date();
    const esHoy = fecha.toDateString() === hoy.toDateString();
    
    if (esHoy) {
      return `Hoy a las ${fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    const opciones = { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' };
    return fecha.toLocaleDateString('es-ES', opciones);
  };

  return (
    <div className="incident-timeline">
      <div className="incident-timeline__header">
        <h3 className="incident-timeline__title">Estado del Proceso</h3>
        <div className="incident-timeline__subtitle">
          Seguimiento del progreso de tu incidencia
        </div>
      </div>

      <div className="incident-timeline__track">
        {ESTADOS_PIPELINE.map((step, index) => {
          const details = getStepDetails(step.key, index);
          const isCompleted = index < estadoActualIndex;
          const isCurrent = index === estadoActualIndex;
          const isPending = index > estadoActualIndex;

          let status = 'pending';
          if (isCompleted) status = 'completed';
          if (isCurrent) status = 'current';

          return (
            <div 
              key={step.key} 
              className={`incident-timeline__step incident-timeline__step--${status}`}
            >
              {/* Línea conectora (no para el último) */}
              {index < ESTADOS_PIPELINE.length - 1 && (
                <div className={`incident-timeline__connector incident-timeline__connector--${
                  isCompleted ? 'completed' : 'pending'
                }`} />
              )}

              {/* Nodo del paso */}
              <div className="incident-timeline__node">
                <div className="incident-timeline__icon">
                  {status === 'completed' ? '✓' : step.icon}
                </div>
              </div>

              {/* Contenido del paso */}
              <div className="incident-timeline__content">
                <div className="incident-timeline__label">{step.label}</div>
                
                {(isCurrent || isCompleted) && details.nota && (
                  <div className="incident-timeline__note">{details.nota}</div>
                )}
                
                {!isCurrent && !isCompleted && (
                  <div className="incident-timeline__description">{step.descripcion}</div>
                )}
                
                {details.fecha && (
                  <div className="incident-timeline__date">
                    {formatFecha(details.fecha)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Indicador de prioridad */}
      {incidencia.prioridad === 'alta' && estadoActualIndex < 2 && (
        <div className="incident-timeline__alert">
          <span className="incident-timeline__alert-icon">⚠️</span>
          <span>Incidencia de prioridad alta - Resolución prioritaria</span>
        </div>
      )}
    </div>
  );
}

export default IncidentTimeline;
